import { Box } from '@material-ui/core';
import { ISummaryBlockData } from 'modules/payrollProcessorHub/components/SheetSummary/SheetSummary';
import { SummaryBlock } from 'modules/payrollProcessorHub/components/SheetSummary/SummaryBlock';
import { IGroupedSheetCalculation } from 'modules/payrollProcessorHub/store/model';
import React from 'react';
import { useSelector } from 'react-redux';
import { emptyFormattedTime } from 'shared/models/DateTime';
import { PayType } from 'shared/models/PaySettings';
import { EntryType } from 'shared/models/sheet/Sheet';
import { formatDollars } from 'shared/utils/formatters/dollarFormatter';
import { getTotalHours } from 'shared/utils/helpers/hoursCalculations';
import { selectAssignmentById } from 'store/entities/configuration/configurationSelectors';

export interface ISheetGroupTotalByTypeProp {
    sheetGroup: IGroupedSheetCalculation;
    type: EntryType;
}

/**
 * Calculate total dollars in payment for time activity
 * @param {(string | null)[]} dollarsComponents
 * @return number
 */
const calculateTotalTimeDollars = (dollarsComponents: (string | null)[]) => (
    dollarsComponents.reduce((result: number, componentValue: string | null) => {
        if (!componentValue) {
            return result;
        }
        const floatValue = parseFloat(componentValue);
        return !isNaN(floatValue) ? result + floatValue : result;
    }, 0).toFixed(2)
);

export const SheetGroupTotalByType = ({ sheetGroup, type }: ISheetGroupTotalByTypeProp) => {
    const totalTimeDollars = calculateTotalTimeDollars([
        sheetGroup.total_dollars,
        '-' + sheetGroup.expense_total_dollars,
    ]);
    const totalMiles = parseFloat(sheetGroup.expense_total_miles || '');
    const totalHours = getTotalHours([
        sheetGroup.time_ot_hours,
        sheetGroup.time_rt_hours,
        sheetGroup.time_dt_hours,
        sheetGroup.time_salaried_hours,
        sheetGroup.time_holiday_hours,
    ]);
    const assignment = useSelector(selectAssignmentById(sheetGroup.assignment_id));
    const isSalaried = assignment?.pay_type === PayType.Salaried;

    const blocks: ISummaryBlockData[] = [
        {
            value: totalMiles.toString(),
            title: 'Total Miles',
            color: 'blue',
            visible: type === EntryType.EXPENSE && totalMiles,
        },
        {
            value: formatDollars(sheetGroup.expense_total_dollars),
            title: 'Total Amount',
            color: 'green',
            visible: type === EntryType.EXPENSE,
        },
        {
            value: totalHours,
            title: 'Total Hours',
            color: 'blue',
            visible: type === EntryType.TIME && totalHours !== emptyFormattedTime,
        },
        {
            value: sheetGroup?.time_files,
            title: 'Total Files',
            color: 'blue',
            visible: type === EntryType.TIME && sheetGroup?.time_files,
        },
        {
            value: formatDollars(isSalaried ? assignment.pay_rate_value : totalTimeDollars),
            title: 'Total Pay',
            color: 'green',
            visible: type === EntryType.TIME,
        },
    ].filter(block => block.visible) as ISummaryBlockData[];

    return (
        <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            mt={-1}
        >
            {blocks.map(block => (
                <SummaryBlock
                    key={block.title}
                    value={block.value}
                    title={block.title}
                    color={block.color}
                />
            ))}
        </Box>
    );
};
